import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: `By accessing or using the MAX-Sourcing website (the "Site"), you agree to be bound by these Terms of Use and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this Site. The materials contained in this Site are protected by applicable copyright and trademark law.`,
  },
  {
    title: '2. Use of the Site',
    content: `This Site is provided for informational and business inquiry purposes only. You may use the Site solely for lawful purposes and in accordance with these Terms. You agree not to use the Site in any way that violates applicable local, national, or international law or regulation; to transmit any unsolicited or unauthorized advertising or promotional material; or to engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Site.`,
  },
  {
    title: '3. Intellectual Property',
    content: `All content on this Site — including but not limited to text, graphics, logos, images, and software — is the property of MAX-Sourcing or its content suppliers and is protected by Canadian and international intellectual property laws. The MAX-Sourcing name and logo are registered trademarks. Unauthorized use of any materials on this Site may violate copyright, trademark, and other laws.`,
  },
  {
    title: '4. Confidentiality',
    content: `MAX-Sourcing is committed to maintaining the confidentiality of all client and supplier information. Any information submitted through this Site, including through contact or supplier application forms, will be treated with strict confidentiality and will not be disclosed to third parties without your prior consent, except as required by law. We do not sell, trade, or rent your personal information to others.`,
  },
  {
    title: '5. Disclaimer of Warranties',
    content: `The materials on this Site are provided on an "as is" basis. MAX-Sourcing makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights. MAX-Sourcing does not warrant or make any representations concerning the accuracy, likely results, or reliability of the use of the materials on its Site or otherwise relating to such materials or on any sites linked to this Site.`,
  },
  {
    title: '6. Limitation of Liability',
    content: `In no event shall MAX-Sourcing or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on this Site, even if MAX-Sourcing or an authorized representative has been notified orally or in writing of the possibility of such damage.`,
  },
  {
    title: '7. Links to Third-Party Sites',
    content: `MAX-Sourcing has not reviewed all of the sites linked to its Site and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by MAX-Sourcing of the site. Use of any such linked website is at the user's own risk.`,
  },
  {
    title: '8. Privacy & Cookies',
    content: `When you visit our Site, we may collect certain information automatically, including cookies and similar tracking technologies, to improve your browsing experience and analyze Site traffic. By continuing to use this Site, you consent to our use of cookies as described in our Cookie Policy. We use only essential and analytics cookies — we do not use advertising or tracking cookies. You may disable cookies through your browser settings, though this may affect certain Site functionality.`,
  },
  {
    title: '9. Governing Law',
    content: `These Terms of Use are governed by and construed in accordance with the laws of Canada, and you irrevocably submit to the exclusive jurisdiction of its courts. MAX-Sourcing operates in compliance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable privacy legislation.`,
  },
  {
    title: '10. Modifications',
    content: `MAX-Sourcing may revise these Terms of Use at any time without notice. By using this Site, you agree to be bound by the then-current version of these Terms of Use. We encourage you to review this page periodically for any changes. Your continued use of the Site following the posting of changes constitutes your acceptance of those changes.`,
  },
  {
    title: '11. Contact',
    content: `If you have any questions about these Terms of Use, please contact us through the Contact page on this Site. We are committed to responding to all inquiries as quickly as possible.`,
  },
];

const TERMS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/terms#webpage`,
  url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/terms`,
  name: 'Terms of Use & Privacy | MAX-Sourcing',
  description:
    'Read the Terms of Use and Privacy Policy for the MAX-Sourcing website. Governed by Quebec, Canada law under PIPEDA.',
  inLanguage: 'en',
  isPartOf: {
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#website`,
  },
  dateModified: '2026-04-07',
  about: {
    '@type': 'Organization',
    name: 'MAX-Sourcing',
    url: import.meta.env.VITE_SITE_URL ?? 'https://example.com',
  },
  translationOfWork: {
    '@type': 'WebPage',
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/conditions#webpage`,
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/conditions`,
    inLanguage: 'fr',
  },
  workTranslation: {
    '@type': 'WebPage',
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/conditions#webpage`,
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/conditions`,
    inLanguage: 'fr',
  },
};

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Use & Privacy | MAX-Sourcing';
    window.scrollTo(0, 0);
    const id = 'schema-terms';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(TERMS_SCHEMA);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Hero */}
      <section className="w-full bg-stone-800 pt-36 pb-16 px-6 md:px-16 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #10b981 0%, transparent 60%)' }}
        />
        <div className="max-w-4xl mx-auto relative">
          <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest block mb-4">Legal</span>
          <h1
            className="text-4xl md:text-5xl font-extrabold text-white mb-4"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Terms of Use &amp; Privacy
          </h1>
          <p className="text-white/60 text-sm leading-relaxed max-w-xl">
            Last updated: April 2026. Please read these terms carefully before using the MAX-Sourcing website.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="w-full py-16 px-6 md:px-16 flex-1">
        <div className="max-w-4xl mx-auto">

          {/* Intro */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-6 mb-12">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-information-line text-emerald-600 text-xl" />
              </div>
              <p className="text-stone-700 text-sm leading-relaxed">
                These Terms of Use govern your access to and use of the MAX-Sourcing website. MAX-Sourcing is a sourcing & supply company of ingredients and fine chemicals company headquartered in Canada, operating under Canadian law. For questions, please visit our{' '}
                <Link to="/contact" className="text-emerald-700 font-semibold hover:underline cursor-pointer">
                  Contact page
                </Link>.
              </p>
            </div>
          </div>

          {/* Table of contents */}
          <div className="bg-stone-50 border border-stone-100 rounded-lg p-6 mb-12">
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4">Table of Contents</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {sections.map((s) => (
                <li key={s.title}>
                  <a
                    href={`#${s.title.replace(/\s+/g, '-').toLowerCase()}`}
                    className="text-sm text-emerald-700 hover:underline cursor-pointer"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections.map((s) => (
              <div
                key={s.title}
                id={s.title.replace(/\s+/g, '-').toLowerCase()}
                className="border-b border-stone-100 pb-10 last:border-0"
              >
                <h2
                  className="text-xl font-extrabold text-stone-800 mb-4"
                  style={{ fontFamily: "'DM Serif Display', serif" }}
                >
                  {s.title}
                </h2>
                <p className="text-stone-600 text-sm leading-relaxed">{s.content}</p>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 bg-stone-50 rounded-lg border border-stone-100 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="text-stone-800 font-bold text-base mb-1">Have questions about these terms?</p>
              <p className="text-stone-500 text-sm">Our team is happy to clarify anything — reach out directly.</p>
            </div>
            <Link
              to="/contact"
              className="whitespace-nowrap bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm px-7 py-3 rounded-md transition-colors cursor-pointer"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
